import { makeStyles } from '@material-ui/core';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
  diffItems,
  pluralize,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: 'var(--bui-space-1)',
  },
});

const tagIsHoldback = (tag: string) => {
  const [surface, , holdback] = tag.split('/');
  return (
    !!surface &&
    !!holdback &&
    holdback === 'holdbacks' &&
    surface === 'surfaces'
  );
};

const tagIsExclusivityGroup = (tag: string) => {
  const [surface, , exclusivityGroups] = tag.split('/');
  return (
    !!surface &&
    !!exclusivityGroups &&
    exclusivityGroups === 'exclusivityGroups' &&
    surface === 'surfaces'
  );
};

interface SegmentData {
  allocation?: {
    exclusiveTo?: string[];
  };
}

const getExclusiveTo = (value: unknown): string[] => {
  // The value is the segment object directly at /module_data/flags/segment
  const segment = value as SegmentData | null;
  return segment?.allocation?.exclusiveTo ?? [];
};

const categorizeTags = (tags: string[]) => {
  return {
    exclusivityGroups: tags.filter(tagIsExclusivityGroup),
    holdbacks: tags.filter(tagIsHoldback),
  };
};

const SegmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();

  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  return (
    <>
      {addedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralize(addedCategorized.exclusivityGroups.length, 'exclusivity group')}`}
        >
          <div className={classes.container}>
            {addedCategorized.exclusivityGroups.map(tag => (
              <ActivityTagItem
                key={tag}
                tagLabel={extractLastNameComponent(tag) || tag}
                ariaLabel="Exclusivity group"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {addedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralize(addedCategorized.holdbacks.length, 'holdback')}`}
        >
          <div className={classes.container}>
            {addedCategorized.holdbacks.map(tag => (
              <ActivityTagItem
                key={tag}
                tagLabel={extractLastNameComponent(tag) || tag}
                ariaLabel="Holdback"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {removedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralize(removedCategorized.exclusivityGroups.length, 'exclusivity group')}`}
        >
          <div className={classes.container}>
            {removedCategorized.exclusivityGroups.map(tag => (
              <ActivityTagItem
                key={tag}
                tagLabel={extractLastNameComponent(tag) || tag}
                ariaLabel="Exclusivity group"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}

      {removedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralize(removedCategorized.holdbacks.length, 'holdback')}`}
        >
          <div className={classes.container}>
            {removedCategorized.holdbacks.map(tag => (
              <ActivityTagItem
                key={tag}
                tagLabel={extractLastNameComponent(tag) || tag}
                ariaLabel="Holdback"
              />
            ))}
          </div>
        </ActivityItem.Section>
      )}
    </>
  );
};

const segmentChangePredicate = ({
  change,
}: {
  change: Pick<
    ActivityChangeOptions['change'],
    'fieldPath' | 'oldValue' | 'newValue'
  >;
}) => {
  if (change.fieldPath !== '/module_data/flags/segment') {
    return false;
  }

  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  // Only show if there are holdback or exclusivity group changes
  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  const hasRelevantChanges =
    addedCategorized.holdbacks.length > 0 ||
    addedCategorized.exclusivityGroups.length > 0 ||
    removedCategorized.holdbacks.length > 0 ||
    removedCategorized.exclusivityGroups.length > 0;

  return hasRelevantChanges;
};

export const SEGMENT_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'segment-change',
  priority: 10,
  predicate: segmentChangePredicate,
  render: SegmentChangeRender,
});

// Export for testing
export { segmentChangePredicate };
